import { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { CustomAlert } from 'src/components/CustomAlert';
import { ASSET_TYPE } from 'src/data/asset';
import { isApplicationModeInfinity } from 'src/data/system';
import { useScrollIntoViewConditionally } from 'src/lib/hooks';

import { TEXT_EDITOR_TYPE } from '../../lib';

import { CopyInputActions } from './components/CopyInputActions';
import { CopyInputInfinityActions } from './components/CopyInputInfinityActions';
import { TextEditor } from './components/TextEditor';
import { useHandlers } from './useHandlers';
import { useStyles } from './useStyles';

const propTypes = {
  className: PropTypes.string,
  assetType: PropTypes.oneOf(Object.values(ASSET_TYPE)).isRequired,
  editorContent: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape()), PropTypes.string]),
  editorMarkup: PropTypes.arrayOf(PropTypes.shape()),
  onEditorContentChange: PropTypes.func,
  disabled: PropTypes.bool,
  textEditorType: PropTypes.oneOf(Object.values(TEXT_EDITOR_TYPE)),
  readOnly: PropTypes.bool,
  detectedBrandTerms: PropTypes.array,
  requireChangesConfirmation: PropTypes.bool,
  editorRef: PropTypes.shape(),
  onCancelChanges: PropTypes.func,
  onSaveChanges: PropTypes.func,
  applicationMode: PropTypes.string,
  onEditorCopy: PropTypes.func,
  onEditorReferenceChange: PropTypes.func,
  onTextCopy: PropTypes.func,
  resetCounter: PropTypes.number,
  applyingAnalyzedToneOfVoice: PropTypes.bool,
  successMessage: PropTypes.string,
  onStartEditing: PropTypes.func,
  editMode: PropTypes.bool,
  setEditMode: PropTypes.func,
};

const defaultProps = {
  className: null,
  editorContent: null,
  editorMarkup: null,
  onEditorContentChange: null,
  disabled: false,
  textEditorType: null,
  readOnly: false,
  detectedBrandTerms: null,
  requireChangesConfirmation: false,
  editorRef: null,
  onCancelChanges: null,
  onSaveChanges: null,
  applicationMode: null,
  onEditorCopy: null,
  onEditorReferenceChange: null,
  onTextCopy: null,
  resetCounter: 0,
  applyingAnalyzedToneOfVoice: false,
  successMessage: null,
  onStartEditing: null,
  editMode: false,
  setEditMode: null,
};

export const CopyInput = ({
  className,
  assetType,
  editorContent,
  editorMarkup,
  onEditorContentChange,
  disabled,
  textEditorType,
  readOnly,
  detectedBrandTerms,
  requireChangesConfirmation,
  editorRef,
  onCancelChanges,
  onSaveChanges,
  applicationMode,
  onEditorCopy,
  onEditorReferenceChange,
  onTextCopy,
  resetCounter,
  applyingAnalyzedToneOfVoice,
  successMessage,
  onStartEditing,
  editMode,
  setEditMode,
}) => {
  const styles = useStyles();

  const [editor, setEditor] = useState();

  const ref = useRef();

  const { handleCancelClick, handleSaveClick, handleEditorContentChange, handleEditClick } =
    useHandlers({
      editor,
      setEditMode,
      onCancelChanges,
      onEditorContentChange,
      onSaveChanges,
      onStartEditing,
    });

  useEffect(() => {
    if (onEditorReferenceChange) {
      onEditorReferenceChange(editor);
    }
  }, [editor, onEditorReferenceChange]);

  useEffect(() => {
    if (applyingAnalyzedToneOfVoice && !editMode) {
      setEditMode(true);
    }
  }, [applyingAnalyzedToneOfVoice, editMode, setEditMode]);

  useScrollIntoViewConditionally({
    elementRef: ref,
    condition: successMessage?.length > 0,
    block: 'end',
  });

  const isAppModeInfinity = isApplicationModeInfinity(applicationMode);

  return (
    <div
      ref={ref}
      className={cx(styles.root, className)}
    >
      <TextEditor
        classes={{ editor: styles.editor }}
        assetType={assetType}
        content={editorContent}
        markup={editorMarkup}
        onChange={handleEditorContentChange}
        disabled={disabled}
        editorType={textEditorType}
        readOnly={readOnly || !editMode || applyingAnalyzedToneOfVoice}
        detectedBrandTerms={detectedBrandTerms}
        editorRef={editorRef}
        setEditor={setEditor}
        onCopy={() => onTextCopy?.(editor)}
        onEdit={handleEditClick}
        editMode={editMode}
        isAppModeInfinity={isAppModeInfinity}
        loading={applyingAnalyzedToneOfVoice}
      />

      {successMessage && (
        <CustomAlert
          className={styles.alert}
          classes={{
            message: styles.alertMessage,
            textTitle: styles.alertTitle,
            icon: styles.alertIcon,
          }}
          severity="successBold"
          title={successMessage}
          hideCloseButton
        />
      )}

      {isAppModeInfinity ? (
        <CopyInputInfinityActions
          className={styles.editingActions}
          readOnly={readOnly}
          onSave={handleSaveClick}
          onCopy={onEditorCopy}
          disabled={disabled}
        />
      ) : (
        requireChangesConfirmation &&
        !readOnly &&
        editMode && (
          <CopyInputActions
            className={styles.editingActions}
            editMode={editMode}
            onSave={handleSaveClick}
            onCancel={handleCancelClick}
            disabled={disabled || applyingAnalyzedToneOfVoice}
          />
        )
      )}
    </div>
  );
};

CopyInput.displayName = 'CopyInput';
CopyInput.propTypes = propTypes;
CopyInput.defaultProps = defaultProps;
